/* sk-live-events-hub.js — Live Events Hub controller.
 *
 * Lists every live + upcoming event for the shop. Boots from the shell DOM
 * emitted by sections/sk-live-events-hub.liquid and from
 * window.skLiveEventsHubConfig:
 *   { proxyPath, viewerPath, pollIntervalMs, shopDomain }
 *
 * Backend: GET <proxyPath>/public-list  → { events: [...] }
 *
 * Render contract is idempotent — every poll runs the full grouping +
 * card-render pipeline against the latest response. A failed fetch keeps
 * the previous render and retries on the next interval.
 *
 * Pass 1 polish:
 *  - Hero live-status indicator (pulsing red dot when any event is live;
 *    otherwise "{N} EVENT(S) SCHEDULED").
 *  - Stats strip with live + upcoming counts.
 *  - Sport-colour-coded cards: SVG sport icon, accent border-left, gradient
 *    wash, countdown chip, CTA button.
 *  - Countdown chips tick once a minute against the latest snapshot.
 *  - Live cards animate with a pulsing outline.
 *
 * Sport keyword → colour + icon resolution is intentionally fuzzy so
 * "Sand Volleyball", "Beach Volleyball", and "Volleyball" all land on the
 * volleyball treatment without exact-string matching.
 */
(function () {
  'use strict';

  var cfg = window.skLiveEventsHubConfig || {};
  var PROXY = cfg.proxyPath || '/apps/sk-services/live-events';
  var VIEWER = cfg.viewerPath || '/pages/live-event-feed';
  var POLL_MS = Math.max(10000, cfg.pollIntervalMs || 30000);
  var COUNTDOWN_TICK_MS = 60 * 1000;

  function $(root, sel) { return (root || document).querySelector(sel); }

  function escapeHtml(value) {
    if (value == null) return '';
    return String(value)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function fmtDateTime(iso) {
    if (!iso) return '';
    var d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      weekday: 'short', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit'
    });
  }

  // ---------------------------------------------------------------------------
  // Sport → colour + icon + display name
  //
  // The map is intentionally tiny — eight sports + a trophy fallback. Lookup
  // matches on `includes()` after lowercasing both sides so multi-word inputs
  // ("Sand Volleyball", "Track & Field") and singular/plural variants work.
  // ---------------------------------------------------------------------------
  var SPORT_TABLE = [
    { match: ['sand volleyball', 'beach volleyball'], color: '#fd6506', iconId: 'sk-icon-volleyball', label: 'Sand Volleyball' },
    { match: ['volleyball'],                          color: '#3e8cc4', iconId: 'sk-icon-volleyball', label: 'Volleyball' },
    { match: ['baseball'],                            color: '#e95144', iconId: 'sk-icon-baseball',   label: 'Baseball' },
    { match: ['softball'],                            color: '#d97700', iconId: 'sk-icon-softball',   label: 'Softball' },
    { match: ['basketball'],                          color: '#f97316', iconId: 'sk-icon-basketball', label: 'Basketball' },
    { match: ['soccer'],                              color: '#2f8f5b', iconId: 'sk-icon-soccer',     label: 'Soccer' },
    { match: ['golf'],                                color: '#1a6b3d', iconId: 'sk-icon-golf',       label: 'Golf' },
    { match: ['pickleball', 'pickle ball'],           color: '#ca8a04', iconId: 'sk-icon-pickleball', label: 'Pickleball' },
    { match: ['track', 'field'],                      color: '#e95144', iconId: 'sk-icon-track',      label: 'Track & Field' }
  ];

  function sportMetaFor(rawSport, fallbackTitle) {
    var lookup = String(rawSport || fallbackTitle || '').toLowerCase().trim();
    if (lookup) {
      for (var i = 0; i < SPORT_TABLE.length; i++) {
        var row = SPORT_TABLE[i];
        for (var j = 0; j < row.match.length; j++) {
          if (lookup.indexOf(row.match[j]) !== -1) {
            return { color: row.color, iconId: row.iconId, label: (rawSport ? rawSport : row.label) };
          }
        }
      }
    }
    return { color: '#fd6506', iconId: 'sk-icon-trophy', label: rawSport || 'Event' };
  }

  // ---------------------------------------------------------------------------
  // Countdown helpers
  //
  // `countdownFromNow(iso)` returns a short pre-formatted string like
  //   "Live now", "Starts in 2h 15m", "Starts in 3d 4h", or "" when there
  // is no countdown to render (event already started + has no end_at).
  // ---------------------------------------------------------------------------
  function countdownFromNow(startsIso, endsIso, status) {
    if (!startsIso) return '';
    var start = new Date(startsIso).getTime();
    var end = endsIso ? new Date(endsIso).getTime() : 0;
    if (isNaN(start)) return '';
    var now = Date.now();
    var msLeft = start - now;

    if (status === 'live') return 'Live now';
    if (msLeft <= 0) {
      // Event already passed its start. Only render if it has an end time
      // ahead of us (i.e. it's mid-run but not flipped to status=live yet).
      if (end && end > now) return 'Underway';
      return '';
    }

    var s = Math.floor(msLeft / 1000);
    var d = Math.floor(s / 86400); s -= d * 86400;
    var h = Math.floor(s / 3600);  s -= h * 3600;
    var m = Math.floor(s / 60);

    if (d > 0) return 'Starts in ' + d + 'd ' + h + 'h';
    if (h > 0) return 'Starts in ' + h + 'h ' + m + 'm';
    if (m > 0) return 'Starts in ' + m + 'm';
    return 'Starts shortly';
  }

  function partition(events) {
    var live = [];
    var upcoming = [];
    for (var i = 0; i < events.length; i++) {
      var e = events[i];
      if (e && e.status === 'live') live.push(e);
      else if (e && e.status === 'upcoming') upcoming.push(e);
    }
    return { live: live, upcoming: upcoming };
  }

  function directorName(header) {
    if (!header || !header.director) return '';
    if (typeof header.director === 'string') return header.director;
    if (header.director.name) return header.director.name;
    return '';
  }

  // ---------------------------------------------------------------------------
  // Card markup
  // ---------------------------------------------------------------------------
  function svgUse(id, klass) {
    return '<svg class="' + (klass || '') + '" aria-hidden="true" focusable="false"><use href="#' + id + '"/></svg>';
  }

  function renderCard(event) {
    var header = event.header || {};
    var statusKey = event.status === 'live' ? 'live' : 'upcoming';
    var statusLabel = statusKey === 'live' ? 'LIVE' : 'UPCOMING';
    var sportMeta = sportMetaFor(header.sport, event.title);
    var location = header.location ? String(header.location).trim() : '';
    var director = directorName(header);
    var when = event.starts_at ? fmtDateTime(event.starts_at) : '';
    var countdown = countdownFromNow(event.starts_at, event.ends_at, statusKey);
    var href = VIEWER + '?event_slug=' + encodeURIComponent(event.slug || '');

    var styleAttr = ' style="--card-sport-color: ' + sportMeta.color + ';"';

    var html = '';
    html += '<a class="sk-leh-card" href="' + escapeHtml(href) + '" data-status="' + statusKey + '"' + styleAttr + ' data-starts-at="' + escapeHtml(event.starts_at || '') + '" data-ends-at="' + escapeHtml(event.ends_at || '') + '">';

    html += '<div class="sk-leh-card__top">';
    html +=   '<span class="sk-leh-card__sport-icon-wrap">';
    html +=     svgUse(sportMeta.iconId, 'sk-leh-card__sport-icon');
    html +=   '</span>';
    html +=   '<span class="sk-leh-card__sport-text">';
    html +=     '<span class="sk-leh-card__sport">' + escapeHtml(sportMeta.label) + '</span>';
    html +=   '</span>';
    html +=   '<span class="sk-leh-card__status sk-le__eyebrow" data-status="' + statusKey + '">';
    html +=     '<span class="sk-le__dot"></span>';
    html +=     '<span>' + statusLabel + '</span>';
    html +=   '</span>';
    html += '</div>';

    html += '<h3 class="sk-leh-card__title">' + escapeHtml(event.title || 'Live Event') + '</h3>';

    html += '<ul class="sk-leh-card__meta">';
    if (when) {
      html += '<li class="sk-leh-card__meta-item sk-leh-card__meta-item--when">';
      html +=   svgUse('sk-icon-clock', 'sk-leh-card__meta-icon');
      html +=   '<strong>' + escapeHtml(when) + '</strong>';
      if (countdown) {
        html += '<span class="sk-leh-card__countdown' + (statusKey === 'live' ? ' sk-leh-card__countdown--live' : '') + '" data-sk-leh-countdown>' + escapeHtml(countdown) + '</span>';
      }
      html += '</li>';
    }
    if (location) {
      html += '<li class="sk-leh-card__meta-item sk-leh-card__meta-item--where">';
      html +=   svgUse('sk-icon-pin', 'sk-leh-card__meta-icon');
      html +=   '<span>' + escapeHtml(location) + '</span>';
      html += '</li>';
    }
    if (director) {
      html += '<li class="sk-leh-card__meta-item sk-leh-card__meta-item--who">';
      html +=   svgUse('sk-icon-person', 'sk-leh-card__meta-icon');
      html +=   '<span>Director: <strong>' + escapeHtml(director) + '</strong></span>';
      html += '</li>';
    }
    html += '</ul>';

    html += '<span class="sk-leh-card__cta" aria-hidden="true">';
    html +=   (statusKey === 'live' ? 'Watch live' : 'View bracket');
    html +=   svgUse('sk-icon-arrow');
    html += '</span>';

    html += '</a>';
    return html;
  }

  // ---------------------------------------------------------------------------
  // Renders for hero status, stats, groups
  // ---------------------------------------------------------------------------
  function renderHeroStatus(root, liveCount, upcomingCount) {
    var el = $(root, '[data-sk-leh-hero-status]');
    var txt = $(root, '[data-sk-leh-hero-status-text]');
    if (!el || !txt) return;
    if (liveCount > 0) {
      el.setAttribute('data-live', 'true');
      el.removeAttribute('hidden');
      txt.textContent = liveCount + (liveCount === 1 ? ' event live now' : ' events live now');
      return;
    }
    if (upcomingCount > 0) {
      el.setAttribute('data-live', 'false');
      el.removeAttribute('hidden');
      txt.textContent = upcomingCount + (upcomingCount === 1 ? ' event scheduled' : ' events scheduled');
      return;
    }
    el.setAttribute('data-live', 'false');
    el.setAttribute('hidden', '');
  }

  function renderStats(root, liveCount, upcomingCount) {
    var bar = $(root, '[data-sk-leh-stats]');
    var liveStat = $(root, '[data-sk-leh-stat-live]');
    var upStat = $(root, '[data-sk-leh-stat-upcoming]');
    var liveNum = $(root, '[data-sk-leh-stat-live-count]');
    var upNum = $(root, '[data-sk-leh-stat-upcoming-count]');
    if (!bar) return;
    var hasAny = (liveCount + upcomingCount) > 0;
    if (!hasAny) { bar.setAttribute('hidden', ''); return; }
    bar.removeAttribute('hidden');
    if (liveStat) {
      if (liveCount > 0) liveStat.removeAttribute('hidden');
      else liveStat.setAttribute('hidden', '');
    }
    if (upStat) {
      if (upcomingCount > 0) upStat.removeAttribute('hidden');
      else upStat.setAttribute('hidden', '');
    }
    if (liveNum) liveNum.textContent = String(liveCount);
    if (upNum) upNum.textContent = String(upcomingCount);
  }

  function renderGroup(root, key, list) {
    var section = $(root, '[data-sk-leh-group="' + key + '"]');
    var grid = $(root, '[data-sk-leh-' + key + '-grid]');
    var count = $(root, '[data-sk-leh-' + key + '-count]');
    if (!section || !grid) return;
    if (!list.length) {
      section.setAttribute('hidden', '');
      grid.innerHTML = '';
      if (count) count.textContent = '';
      return;
    }
    section.removeAttribute('hidden');
    grid.innerHTML = list.map(renderCard).join('');
    if (count) count.textContent = list.length + (list.length === 1 ? ' event' : ' events');
  }

  function render(root, events) {
    var groups = partition(events);
    var skeleton = $(root, '[data-sk-leh-skeleton]');
    var body = $(root, '[data-sk-leh-body]');
    var empty = $(root, '[data-sk-leh-empty]');

    if (skeleton) skeleton.setAttribute('hidden', '');
    root.setAttribute('data-state', 'ready');

    var liveCount = groups.live.length;
    var upcomingCount = groups.upcoming.length;
    var hasAny = (liveCount + upcomingCount) > 0;

    renderHeroStatus(root, liveCount, upcomingCount);
    renderStats(root, liveCount, upcomingCount);

    if (hasAny) {
      if (body) body.removeAttribute('hidden');
      if (empty) empty.setAttribute('hidden', '');
    } else {
      if (body) body.setAttribute('hidden', '');
      if (empty) empty.removeAttribute('hidden');
    }

    renderGroup(root, 'live', groups.live);
    renderGroup(root, 'upcoming', groups.upcoming);
  }

  // ---------------------------------------------------------------------------
  // Countdown tick — re-formats card countdown chips once a minute without a
  // full re-render. Reads starts_at / ends_at off the card element so it
  // stays in sync with the latest poll payload.
  // ---------------------------------------------------------------------------
  function tickCountdowns(root) {
    var cards = root.querySelectorAll('.sk-leh-card');
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      var chip = card.querySelector('[data-sk-leh-countdown]');
      if (!chip) continue;
      var text = countdownFromNow(
        card.getAttribute('data-starts-at') || '',
        card.getAttribute('data-ends-at') || '',
        card.getAttribute('data-status') || ''
      );
      if (text) {
        chip.textContent = text;
        chip.removeAttribute('hidden');
      } else {
        chip.setAttribute('hidden', '');
      }
    }
  }

  function boot(root) {
    var lastSerialized = '';

    function tick() {
      fetchPublicList()
        .then(function (payload) {
          var events = (payload && payload.events) || [];
          var serialized = JSON.stringify(events);
          if (serialized === lastSerialized) return;
          lastSerialized = serialized;
          render(root, events);
        })
        .catch(function (err) {
          if (window && window.console) console.warn('[sk-live-events-hub] poll failed:', err);
        });
    }

    tick();
    setInterval(tick, POLL_MS);
    setInterval(function () { tickCountdowns(root); }, COUNTDOWN_TICK_MS);
    checkAdminStatus(root);
  }

  // ---------------------------------------------------------------------------
  // Admin shortcut
  //
  // Probes /me once on mount. If the signed-in customer is on the live-event
  // allowlist (role !== null), reveal the gold admin pill in the hero. The
  // pill is a plain <a href="…/live-feed-admin"> so it stays accessible and
  // works even with JS errors mid-render.
  // ---------------------------------------------------------------------------
  function checkAdminStatus(root) {
    fetch(PROXY + '/me', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(function (r) {
      if (!r.ok) throw new Error('me HTTP ' + r.status);
      return r.json();
    }).then(function (payload) {
      if (payload && payload.role) revealAdminPill(root);
    }).catch(function (err) {
      if (window && window.console) console.warn('[sk-live-events-hub] /me check failed:', err);
    });
  }

  function revealAdminPill(root) {
    var pill = $(root, '[data-sk-leh-admin]');
    if (pill) pill.removeAttribute('hidden');
  }

  function fetchPublicList() {
    return fetch(PROXY + '/public-list', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(function (r) {
      if (!r.ok) throw new Error('public-list HTTP ' + r.status);
      return r.json();
    });
  }

  function init() {
    var root = document.querySelector('[data-sk-live-events-hub]');
    if (!root) return;
    boot(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}());
